import React, { FC, MouseEventHandler, useRef } from 'react'
import classNames from 'classnames'

import { callAll, getStyledElementProps } from '@/utils'
import { IconMoon, Typography } from '@/atoms'
import { Spacer } from '@/layout'
import { AccordionProps, AccordionBodyProps, AccordionCardProps } from '@/types'

import styles from './accordion.module.scss'

export const AccordionBody: FC<AccordionBodyProps> = (props) => {
  const styledProps = getStyledElementProps(props, styles['body'])
  return <div {...styledProps} />
}

export const Accordion: FC<AccordionProps> = ({
  title,
  subtitle,
  children,
  subtitleColor,
  expanded = false,
  titleVariant = 'emphasized-1',
  headerWithBorder = true,
  mainWrapperProps = {},
  headerWrapperProps = {},
}) => {
  const mainWrapperEl = useRef<HTMLDivElement>(null)

  const handleClick: MouseEventHandler<HTMLDivElement> = () => {
    mainWrapperEl.current?.classList.toggle(styles['expanded'])
  }

  const getHeaderWrapperProps = () => {
    const { onClick, ...props } = headerWrapperProps
    return {
      onClick: callAll(onClick, handleClick),
      ...props,
    }
  }

  const headerStyledProps = getStyledElementProps(
    getHeaderWrapperProps(),
    classNames(styles['header'], {
      [styles['with-border']]: headerWithBorder,
    })
  )

  const styledMainWrapperProps = getStyledElementProps(
    mainWrapperProps,
    classNames({
      [styles['expanded']]: expanded,
    })
  )

  return (
    <div ref={mainWrapperEl} {...styledMainWrapperProps}>
      <div {...headerStyledProps}>
        {title && (
          <Typography variant={titleVariant} tag={'span'}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <>
            <Spacer variant={'vertical'} size={'2x'} />
            <Typography variant={'body-1'} typographyColor={subtitleColor}>
              {subtitle}
            </Typography>
          </>
        )}
        <IconMoon
          name={'icon-chevron-down'}
          size={'xl'}
          className={styles['caret-icon']}
        />
      </div>
      {children}
    </div>
  )
}

export const AccordionCard: FC<AccordionCardProps> = ({
  title,
  children,
  divProps = {},
}) => {
  const rootStyledProps = getStyledElementProps(divProps, styles['root'])
  return (
    <div {...rootStyledProps}>
      <div className={styles['title']}>
        <Typography variant={'strong-1'} typographyColor={'gray-4'}>
          {title}
        </Typography>
        {children}
      </div>
    </div>
  )
}
